import { Skeleton } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useGetOrganizationLoanProductsByCreditFacilityQuery } from '~/packages/organization/requests/organization.gql.generated';

type SlugsToNames = {
  creditFacilities: Map<string, string>;
  loanProducts: Map<string, string>;
};

const SlugToNameContext = createContext<SlugsToNames | null>(null);

export const SlugToNameProvider = ({
  children,
  organizationId,
  loadingComponent,
}: {
  children: React.ReactNode;
  organizationId: string;
  loadingComponent?: React.ReactNode;
}) => {
  const getOrgFacilityLoanProductQuery = useGetOrganizationLoanProductsByCreditFacilityQuery(
    { variables: { organizationId } },
  );
  if (getOrgFacilityLoanProductQuery.error) {
    throw getOrgFacilityLoanProductQuery.error;
  }

  const slugToNames: SlugsToNames | null = useMemo(() => {
    if (!getOrgFacilityLoanProductQuery.data) {
      return null;
    }

    const creditFacilities = new Map<string, string>();
    const loanProducts = new Map<string, string>();

    getOrgFacilityLoanProductQuery.data.result.creditFacilities.forEach(
      (cf) => {
        creditFacilities.set(cf.slug, cf.name);
        cf.loanProducts.forEach((lp) => {
          loanProducts.set(lp.slug, lp.name);
        });
      },
    );

    return { creditFacilities, loanProducts };
  }, [getOrgFacilityLoanProductQuery.data]);

  if (!slugToNames) {
    return (
      loadingComponent ?? (
        <Skeleton height="600px" data-testid="slugToNameProvider-loading" />
      )
    );
  }

  return (
    <SlugToNameContext.Provider value={slugToNames}>
      {children}
    </SlugToNameContext.Provider>
  );
};

export const useSlugToName = () => {
  const context = useContext(SlugToNameContext);
  finleyAssert(
    context,
    'useSlugToName must be used within a SlugToNameProvider',
  );

  return useMemo(() => {
    return {
      // These map slugs to human-readable display labels. A slug may be absent
      // from the map (e.g. a pipeline graph that references a loan product /
      // facility not surfaced by the org's active credit facilities), so fall
      // back to the slug rather than throwing — a missing display label must
      // not crash the surrounding UI.
      cfSlugToName: (slug: string): string =>
        context.creditFacilities.get(slug) ?? slug,
      lpSlugToName: (slug: string): string =>
        context.loanProducts.get(slug) ?? slug,
    };
  }, [context.creditFacilities, context.loanProducts]);
};
